import React, { useEffect, useState, useRef, FunctionComponent, useCallback } from 'react';
import ElementsShowInViewContext from '../ElementsShowInViewContext';

const scrollThrottleTimer = 100;

type ElementsShowInViewProviderProps = {
  /** the element is considered in the view only if its Y is above this value */
  showInViewY?: number;
  observer?: HTMLElement | null;
};

const ElementsShowInViewProvider: FunctionComponent<
  React.PropsWithChildren<ElementsShowInViewProviderProps>
> = ({ children, observer: initialObserver = null, showInViewY = 50 }) => {
  const [observer, setObserver] = useState<HTMLElement | null>(initialObserver);
  const [elementIds, setElementIds] = useState<string[]>([]);
  const [currentElementId, setCurrentElementId] = useState<string | null>(null);
  const removeScrollHandlerRef = useRef<null | (() => void)>(null);
  const isHandleScroll = useRef(true);
  const enabled = useRef(true);
  const isPaused = useRef(false);
  const pauseTimeout = useRef<ReturnType<typeof setTimeout>>();
  const scrollThrottleTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const removeScrollHandler = () => {
      if (removeScrollHandlerRef.current) {
        removeScrollHandlerRef.current();
      }
      // Clear scroll throttle timeout and reset throttle flag
      if (scrollThrottleTimeout.current) {
        clearTimeout(scrollThrottleTimeout.current);
        scrollThrottleTimeout.current = undefined;
      }
      // Reset the throttle flag so scroll handler can run again
      isHandleScroll.current = true;
    };
    removeScrollHandler();
    if (observer && elementIds.length > 0) {
      const scrollHandler = () => {
        if (!isHandleScroll.current || !enabled.current || isPaused.current) {
          return;
        }
        const windowHeight = window.innerHeight;
        // throttle logic, only scrollHandler every scrollThrottleTimer ms
        isHandleScroll.current = false;
        // Clear any existing throttle timeout before setting a new one
        if (scrollThrottleTimeout.current) {
          clearTimeout(scrollThrottleTimeout.current);
        }
        scrollThrottleTimeout.current = setTimeout(() => {
          isHandleScroll.current = true;
          scrollThrottleTimeout.current = undefined;
        }, scrollThrottleTimer);

        let currentId: string | null = null;
        for (let i = 0; i < elementIds.length; i += 1) {
          const elementId = elementIds[i];
          const element = document.getElementById(elementId);
          if (!element) {
            // eslint-disable-next-line no-continue
            continue;
          }
          const rect = element.getBoundingClientRect();
          if (rect.y > windowHeight) {
            break;
          }
          currentId = elementId;
          if (rect.y > showInViewY) {
            break;
          }
        }
        setCurrentElementId(currentId);
      };
      observer.addEventListener('scroll', scrollHandler);
      removeScrollHandlerRef.current = () => observer.removeEventListener('scroll', scrollHandler);
    }

    return removeScrollHandler;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementIds, observer]);

  useEffect(() => {
    setObserver(initialObserver);
  }, [initialObserver]);

  const disable = useCallback(() => {
    enabled.current = false;
  }, []);

  const enable = useCallback(() => {
    enabled.current = true;
  }, []);

  const pause = useCallback((timeInMs: number) => {
    isPaused.current = true;
    if (pauseTimeout.current) {
      clearTimeout(pauseTimeout.current);
    }
    pauseTimeout.current = setTimeout(() => {
      isPaused.current = false;
      pauseTimeout.current = undefined;
    }, timeInMs);
  }, []);

  // Cleanup all timeouts on unmount
  useEffect(() => {
    return () => {
      if (pauseTimeout.current) {
        clearTimeout(pauseTimeout.current);
        pauseTimeout.current = undefined;
      }
      if (scrollThrottleTimeout.current) {
        clearTimeout(scrollThrottleTimeout.current);
        scrollThrottleTimeout.current = undefined;
      }
    };
  }, []);

  return (
    <ElementsShowInViewContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        observer,
        setObserver,
        elementIds,
        setElementIds,
        currentElementId,
        disable,
        enable,
        pause,
      }}>
      {children}
    </ElementsShowInViewContext.Provider>
  );
};

export default ElementsShowInViewProvider;
